import React, { useState, useEffect } from 'react';

import { getUser } from '../../modules/contexts/Auth/RequireAuth';
import httpClient, { isHttpError } from '../../services/httpClient';
import { formatDt } from '../../utils/functions';
import { swal, swalDelete, swalErro } from '../../utils/swal';
import BarraInferiorBotoes from '../BarraInferiorBotoes';
import BoxConteudo from '../BoxConteudo';
import CustomCol from '../CustomCol';
import CustomRow from '../CustomRow';
import Input from '../InputNew';
import Select from '../InputNew/Select';
import Modal from '../Modal';
import Tabela from '../Tabela';
import WorkspaceBotoes from '../WorkspaceBotoes';
import WorkspaceFicha from '../WorkspaceFicha';

interface UploadArquivoProps {
  multiple?: boolean;
  modulo?: string;
  registroId?: number;
  maxFileSizeMB?: number;
  allowedExtensions?: string[];
}

enum TipoPublicidade {
  PUBLICO = 'Público',
  PRIVADO = 'Privado',
  CONFIDENCIAL = 'Confidencial',
}

const mapFileExtensionToTpExtensao = (filename: string): number => {
  const extension = filename.split('.').pop()?.toLowerCase() || '';
  const extensoesMapeadas: Record<string, number> = {
    docx: 1,
    pdf: 2,
    xlsx: 3,
    ppt: 4,
    gsheet: 5,
    png: 6,
    jpeg: 7,
    jpg: 7,
    mp4: 8,
  };
  return extensoesMapeadas[extension] ?? 9;
};

const UploadMultiple: React.FC<UploadArquivoProps> = ({
  multiple = true,
  modulo = '',
  registroId = 0,
  maxFileSizeMB = 15,
  allowedExtensions = ['pdf', 'doc'],
}) => {
  const user = getUser();
  const userID = user?.sub;

  const [arquivos, setArquivos] = useState<any[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [titulo, setTitulo] = useState<string>('');
  const [descricao, setDescricao] = useState<string>('');
  const [tipoPublicidade, setTipoPublicidade] = useState<TipoPublicidade>(
    TipoPublicidade.PUBLICO,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showModalUpload, setShowModalUpload] = useState(false);

  const handleModalUpload = () => setShowModalUpload((prev) => !prev);

  const clearUploadForm = () => {
    setTitulo('');
    setDescricao('');
    setFiles([]);
  };

  useEffect(() => {
    fetchArquivos();
  }, []);

  const fetchArquivos = async () => {
    try {
      setLoading(true);

      const res = await httpClient.get(`/api/arquivo`, {
        params: {
          noTabela: modulo?.toUpperCase() || 'TB_DOCUMENTOS',
          coRegistro: String(registroId || 0),
        },
      });
      setArquivos(res.data.arquivos || []);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao buscar arquivos.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const arquivosSelecionados = Array.from(e.target.files);

    const arquivosInvalidos = arquivosSelecionados.filter((file) => {
      const extensao = file.name.split('.').pop()?.toLowerCase() || '';
      const tamanhoMB = file.size / (1024 * 1024);
      return !allowedExtensions.includes(extensao) || tamanhoMB > maxFileSizeMB;
    });

    if (arquivosInvalidos.length > 0) {
      const mensagens = arquivosInvalidos.map((file) => {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const size = (file.size / (1024 * 1024)).toFixed(2);
        let msg = `• ${file.name} (${size}MB)`;

        if (!allowedExtensions.includes(ext)) {
          msg += ' — tipo de arquivo não permitido';
        } else if (Number(size) > maxFileSizeMB) {
          msg += ` — excede o limite de ${maxFileSizeMB}MB`;
        }

        return msg;
      });

      setError(
        `Os seguintes arquivos não são válidos:\n${mensagens.join('\n')}`,
      );
      setFiles([]);
      return;
    }

    setFiles(arquivosSelecionados);
    setError(null);
  };

  const handleUpload = async (): Promise<boolean> => {
    if (!files.length) {
      setError('Selecione um arquivo.');
      return false;
    }
    if (!titulo.trim()) {
      setError('Título obrigatório.');
      return false;
    }
    if (!descricao.trim()) {
      setError('Descrição obrigatória.');
      return false;
    }

    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      //  Mostrar "loading"
      swal('info', 'Enviando arquivo(s)...', 'Aguarde');

      const tpPublicidadeNumber =
        tipoPublicidade === TipoPublicidade.PUBLICO
          ? 1
          : tipoPublicidade === TipoPublicidade.PRIVADO
            ? 2
            : 3;

      for (const file of files) {
        const tpExtensao = mapFileExtensionToTpExtensao(file.name);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('coUsuarioRegistro', String(userID));
        formData.append('coUsuarioAlteracao', String(userID));
        formData.append('coUsuarioVisualizacao', String(userID));
        formData.append('noTabela', modulo.toUpperCase() || 'TB_DOCUMENTOS');
        formData.append('coRegistro', String(registroId || 0));
        formData.append('noArquivo', file.name);
        formData.append('noTitulo', titulo);
        formData.append('dsDescricao', descricao);
        formData.append('tpPublicidade', tpPublicidadeNumber.toString());
        formData.append('tpExtensao', tpExtensao.toString());
        formData.append('tpArquivo', '2');
        formData.append('dtCriacao', new Date().toISOString().split('T')[0]);
        formData.append('dsObservacao', '');

        await httpClient.post(`/api/arquivo/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      await fetchArquivos();
      clearUploadForm();

      //  Sucesso
      swal('success', 'Upload realizado com sucesso!', 'Sucesso');
      return true;
    } catch (err: any) {
      //  Erro
      const mensagemErro =
        err.response?.data?.message || err.message || 'Erro no upload.';
      swal('error', mensagemErro, 'Erro');
      return false;
    } finally {
      setUploading(false);
    }
  };

  const extensaoLabels: Record<number, string> = {
    1: 'DOCX',
    2: 'PDF',
    3: 'XLSX',
    4: 'PPT',
    5: 'GSheet',
    6: 'PNG',
    7: 'JPEG',
    8: 'MP4',
    9: 'Outros',
  };

  const publicidadeLabels: Record<number, string> = {
    1: 'Público',
    2: 'Privado',
    3: 'Confidencial',
  };

  const headersArquivos = [
    { text: '', width: 2, key: '' },
    { text: 'ID', width: 2, key: 'id' },
    { text: 'Título', width: 10, key: 'titulo' },
    { text: 'Nome do Arquivo', width: 10, key: 'noArquivo' },
    { text: 'Descrição', width: 15, key: 'descricao' },
    { text: 'Autor', width: 10, key: 'autor' },
    { text: 'Extensão', width: 5, key: 'extensao' },
    { text: 'Data de Criação', width: 10, key: 'dataCriacao' },
    { text: 'Publicidade', width: 10, key: 'tipoPublicidade' },

    { text: 'Ações', width: 5, key: 'acoes' },
  ];

  const tableDataArquivos = arquivos.map((item) => ({
    id: item.id,
    Título: item.titulo || 'Sem título',
    'Nome do Arquivo': item.noArquivo?.trim() || 'N/A',
    Descrição: item.descricao || 'Sem descrição',
    Autor: item.usuarioInclusao?.nomeCompleto || 'Desconhecido',
    Extensão: extensaoLabels[item.tpExtensao] || 'Desconhecido',
    'Data de Criação': item.dtCriacao ? formatDt(item.dtCriacao) : 'N/A',
    Publicidade: publicidadeLabels[item.tpPublicidade] || 'Desconhecido',
    // link: item.link,
    Ações: (
      <div style={{ display: 'flex', gap: '10px' }}>
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          title="Visualizar"
          style={{
            background: 'none',
            border: 'none',
            color: '#717171',
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          <span className="material-icons">visibility</span>
        </a>
        <button
          onClick={() => handleDeleteArquivo(item.id)}
          title="Excluir"
          style={{
            background: 'none',
            border: 'none',
            color: '#717171',
            cursor: 'pointer',
          }}
        >
          <span className="material-icons">delete</span>
        </button>
      </div>
    ),
  }));

  const dataOffCanvasArquivos = tableDataArquivos;

  const handleDeleteArquivo = async (id: number) => {
    const confirmed = await swalDelete();

    if (!confirmed) {
      swalErro('A operação foi cancelada.');
      return;
    }

    try {
      const response = await httpClient.delete(`/api/arquivo/${id}`);

      if (response.status === 200 || response.status === 204) {
        await fetchArquivos(); // Atualiza a lista após exclusão
        swal(
          'success',
          response.data?.message || 'Arquivo excluído com sucesso!',
          'Sucesso',
        );
      } else {
        throw new Error('Erro inesperado ao excluir o arquivo.');
      }
    } catch (error) {
      console.error('Erro ao excluir o arquivo:', error);

      if (isHttpError(error)) {
        swalErro(error.response?.data?.message || 'Erro desconhecido');
      } else {
        swalErro('Não foi possível excluir o arquivo. Tente novamente.');
      }
    }
  };

  return (
    <BoxConteudo boxTitulo="Arquivos" fechado={true}>
      <Modal
        tituloModal="Upload de Arquivo"
        icone="upload"
        open={showModalUpload}
        onHide={handleModalUpload}
        modalSize="medio"
        size="lg"
      >
        <WorkspaceFicha>
          <CustomRow>
            <CustomCol width="200px">
              <Select
                name="tipoPublicidade"
                label="Tipo de Publicidade"
                value={tipoPublicidade}
                onChange={(e) =>
                  setTipoPublicidade(e.target.value as TipoPublicidade)
                }
                options={[
                  { label: 'Público', value: TipoPublicidade.PUBLICO },
                  { label: 'Privado', value: TipoPublicidade.PRIVADO },
                  {
                    label: 'Confidencial',
                    value: TipoPublicidade.CONFIDENCIAL,
                  },
                ]}
              />
            </CustomCol>

            <CustomCol>
              <Input
                id="titulo"
                name="titulo"
                label="Título do conjunto de arquivos"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
              />
            </CustomCol>
          </CustomRow>
          <CustomRow>
            <CustomCol>
              <Input
                id="descricao"
                type="textarea"
                linhas={5}
                name="descricao"
                label="Descrição do arquivo"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </CustomCol>
          </CustomRow>
          <CustomRow>
            <CustomCol width="100%" style={{ textAlign: 'center' }}>
              <label
                htmlFor="file-upload"
                style={{
                  display: 'inline-block',
                  padding: '10px 90px',
                  backgroundColor: '#007BFF',
                  color: '#fff',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  transition: 'background 0.3s',
                }}
              >
                Selecionar Arquivo{multiple ? 's' : ''}
              </label>
              <input
                id="file-upload"
                type="file"
                multiple={multiple}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {(maxFileSizeMB || allowedExtensions.length > 0) && (
                <p
                  style={{
                    marginTop: '10px',
                    fontSize: '0.85rem',
                    color: '#666',
                  }}
                >
                  Tamanho máximo: <strong>{maxFileSizeMB}MB</strong>. Tipos
                  permitidos:{' '}
                  <strong>
                    {allowedExtensions
                      .map((ext) => ext.toUpperCase())
                      .join(', ')}
                  </strong>
                  .
                </p>
              )}

              {error && (
                <p
                  style={{
                    color: 'red',
                    marginTop: '5px',
                    fontSize: '0.85rem',
                  }}
                >
                  {error.split('\n').map((line, idx) => (
                    <span key={idx}>
                      {line}
                      <br />
                    </span>
                  ))}
                </p>
              )}

              {files.length > 0 && (
                <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                  {multiple
                    ? `${files.length} arquivos selecionados.`
                    : `Arquivo selecionado: ${files[0].name}`}
                </p>
              )}
            </CustomCol>
          </CustomRow>

          <BarraInferiorBotoes>
            <WorkspaceBotoes
              cancelar
              onClickCancelar={() => {
                handleModalUpload();
                clearUploadForm();
              }}
              save
              onClickSave={() => {
                handleUpload();
                handleModalUpload();
              }}
            />
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </Modal>

      {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
      {uploading}

      <Tabela
        headers={headersArquivos}
        dataTable={tableDataArquivos}
        dataOffCanvas={dataOffCanvasArquivos}
        showPagination={false}
        rowClickable={false}
      />

      <BarraInferiorBotoes>
        {(multiple || arquivos.length === 0) && (
          <WorkspaceBotoes inserir onClickInserir={handleModalUpload} />
        )}
      </BarraInferiorBotoes>
    </BoxConteudo>
  );
};

export default UploadMultiple;
